"use client";

import Link from "next/link";
import { useId, useState } from "react";
import { HUB_SPORT_GROUPS, HUB_SPORTS } from "@/lib/conditions/hub/sports";
import type {
  HubAwardHeadline,
  HubAwardRow,
  HubAwards,
  HubFunAward,
  HubSport,
  HubSportKey,
} from "@/lib/conditions/hub/types";
import { HubArrow, HubChevron, HubIcon } from "./hub-icons";
import { useHubSport } from "./hub-sport-state";

/**
 * F4 season awards card (B4): the highest-rated spot of the year, Best by sport (a native select
 * on phone and desktop alike, following the board's sport until the visitor picks one) and the
 * fun award cycler. Season averages and counts, not today's readings, so no freshness gate.
 *
 * The board sport comes from `boardSport` when the page passes it, else from the shared
 * `?sport=` state (`useHubSport`), so it follows the board island without any wiring.
 */
export type HubAwardsCardProps = Readonly<{
  awards: HubAwards;
  /** The sports the page shows, picker order. */
  sports: readonly HubSport[];
  namedPublic: boolean;
  /** Controlled board sport (null = All). Omit to follow the shared `?sport=` state. */
  boardSport?: HubSportKey | null;
}>;

const FALLBACK_SPORT: HubSportKey = "surf";

/** The sport Best by sport shows: the visitor's pick, else the board's, else surf, else the first. */
export function awardsSportFor(
  options: readonly HubSportKey[],
  picked: HubSportKey | null,
  board: HubSportKey | null,
): HubSportKey | null {
  if (picked && options.includes(picked)) return picked;
  if (board && options.includes(board)) return board;
  if (options.includes(FALLBACK_SPORT)) return FALLBACK_SPORT;
  return options[0] ?? null;
}

const SPORT_BY_KEY: ReadonlyMap<string, HubSport> = new Map(HUB_SPORTS.map((sport) => [sport.key, sport]));
const SPORT_BY_LABEL: ReadonlyMap<string, HubSport> = new Map(HUB_SPORTS.map((sport) => [sport.label, sport]));

function TrophyTile({ variant }: { variant: "desktop" | "phone" }) {
  return (
    <span
      aria-hidden="true"
      className={`flex size-11 shrink-0 items-center justify-center rounded-[12px] text-teal-light ${
        variant === "desktop" ? "bg-[rgba(91,192,206,0.16)]" : "bg-navy"
      }`}
    >
      <HubIcon name={variant === "desktop" ? "trophy" : "trophy-phone"} size={variant === "desktop" ? 26 : 24} strokeWidth={1.7} />
    </span>
  );
}

function headlineAria(headline: HubAwardHeadline, year: number): string {
  return `Highest-rated spot of ${year}: ${headline.winnerName}, ${headline.country}, ${headline.sportLabel}, `
    + `${headline.valueLabel} out of 10 average over ${headline.dataDays} days. ${headline.linkLabel}. Open the award page`;
}

const RING_C = 163.36;

function DesktopHeader({ awards }: { awards: HubAwards }) {
  const headline = awards.headline;
  return (
    <div className="relative hidden overflow-hidden bg-navy px-5 pb-4 pt-5 text-white md:block">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-[90px] -top-[120px] size-[300px] rounded-full bg-[radial-gradient(circle,rgba(22,128,143,0.45)_0%,rgba(22,128,143,0)_70%)]"
      />
      <div className="relative flex items-center gap-3">
        <TrophyTile variant="desktop" />
        <div className="min-w-0 flex-auto">
          <h2 className="m-0 text-[21px] font-bold leading-[1.2] text-white">Season awards {awards.year}</h2>
          <p className="mt-[3px] text-[13px] leading-[1.4] text-[#C9D3E0]">Each sport is ranked on its own. Calendar year {awards.year}.</p>
        </div>
      </div>
      {headline && (
        <>
          <Link
            href={headline.href}
            prefetch={false}
            aria-label={headlineAria(headline, awards.year)}
            className="group relative mt-4 block rounded-[14px] border border-white/16 bg-white/7 px-4 py-3.5 text-white no-underline hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-light"
          >
            <span className="block text-[12px] font-bold uppercase tracking-[0.1em] text-teal-light">
              Highest-rated spot of {awards.year}
            </span>
            <span className="mt-2 flex items-center gap-3">
              <span aria-hidden="true" className="flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-[rgba(91,192,206,0.16)] text-teal-light">
                <HubIcon name={SPORT_BY_KEY.get(headline.sportKey)?.icon ?? "surf"} size={24} strokeWidth={1.7} />
              </span>
              <span className="flex min-w-0 flex-auto flex-col gap-0.5">
                <span className="truncate text-[26px] font-bold leading-[1.1] text-white">{headline.winnerName}</span>
                <span className="text-[14px] text-[#C9D3E0]">{headline.country} · {headline.sportLabel}</span>
              </span>
              <span className="flex shrink-0 items-baseline gap-0.5">
                <span className="text-[40px] font-bold leading-none text-white">{headline.valueLabel}</span>
                <span className="text-[14px] font-semibold text-[#C9D3E0]">/10</span>
              </span>
            </span>
            <span aria-hidden="true" className="mt-3 block h-1.5 overflow-hidden rounded-full bg-white/14">
              <span
                className="block h-1.5 rounded-full bg-[#5FD3B6]"
                style={{ width: `${Math.max(0, Math.min(100, Math.round(headline.value * 10)))}%` }}
              />
            </span>
            <span className="mt-2.5 block text-[13.5px] text-[#D5DCE6]">Average over {headline.dataDays} days</span>
            <span className="mt-1 flex items-center gap-1.5 text-[13.5px] font-bold text-teal-light">
              {headline.linkLabel}
              <HubArrow />
            </span>
          </Link>
          <p className="relative mx-0.5 mt-2.5 text-[12.5px] leading-[1.45] text-[#C9D3E0]">{headline.ruleNote}</p>
        </>
      )}
    </div>
  );
}

function PhoneHeader({ awards }: { awards: HubAwards }) {
  const headline = awards.headline;
  const arc = headline ? Math.round((Math.max(0, Math.min(10, headline.value)) / 10) * RING_C * 100) / 100 : 0;
  return (
    <div className="md:hidden">
      <div className="flex items-start gap-3 px-4 pb-3 pt-4">
        <TrophyTile variant="phone" />
        <div className="min-w-0 flex-auto">
          <h2 className="m-0 text-[20px] font-bold leading-[1.2] text-navy">Season awards {awards.year}</h2>
          <p className="mt-1 text-[13px] leading-[1.45] text-[#566072]">Each sport is ranked on its own. Calendar year {awards.year}.</p>
        </div>
      </div>
      {headline && (
        <div className="relative mx-3 overflow-hidden rounded-2xl bg-navy p-4 text-white">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-[70px] -top-[70px] size-[220px] rounded-full bg-[radial-gradient(circle,rgba(22,128,143,0.5)_0%,rgba(22,128,143,0)_70%)]"
          />
          <h3 className="relative m-0 text-[12px] font-bold uppercase tracking-[0.12em] text-teal-light">
            Highest-rated spot of {awards.year}
          </h3>
          <div className="relative mt-2.5 flex items-center gap-3.5">
            <div className="flex min-w-0 flex-auto flex-col gap-[5px]">
              <p className="m-0 text-[30px] font-bold leading-[1.05] tracking-[-0.01em] text-white [overflow-wrap:anywhere]">{headline.winnerName}</p>
              <p className="m-0 flex flex-wrap items-center gap-1.5 text-[14px] font-semibold text-[#D5DCE6]">
                {headline.country}
                <span aria-hidden="true" className="text-[#8A97AB]">·</span>
                <span className="inline-flex items-center gap-1 text-teal-light">
                  <HubIcon name={SPORT_BY_KEY.get(headline.sportKey)?.icon ?? "surf"} size={16} strokeWidth={1.9} />
                  {headline.sportLabel}
                </span>
              </p>
              <p className="mt-0.5 text-[13.5px] leading-[1.4] text-white">
                <strong className="font-bold">{headline.valueLabel}/10</strong> average over {headline.dataDays} days
              </p>
            </div>
            <span aria-hidden="true" className="relative size-[76px] shrink-0">
              <svg width="76" height="76" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="6" />
                <circle
                  cx="32"
                  cy="32"
                  r="26"
                  fill="none"
                  stroke="#5FD3B6"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={`${arc.toFixed(2)} ${(RING_C - arc).toFixed(2)}`}
                  transform="rotate(-90 32 32)"
                />
              </svg>
              <span className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                <span className="text-[22px] font-bold text-white">{headline.valueLabel}</span>
                <span className="mt-0.5 text-[11px] font-semibold text-[#C9D3E0]">/10</span>
              </span>
            </span>
          </div>
          <Link
            href={headline.href}
            prefetch={false}
            aria-label={headlineAria(headline, awards.year)}
            className="group relative mt-1.5 inline-flex min-h-11 items-center gap-1.5 text-[14px] font-bold text-teal-light underline underline-offset-[3px] hover:text-teal-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-light"
          >
            {headline.linkLabel}
            <HubArrow />
          </Link>
          <p className="relative mt-0.5 border-t border-white/14 pt-2.5 text-[12px] leading-[1.45] text-[#C9D3E0]">{headline.ruleNote}</p>
        </div>
      )}
    </div>
  );
}

function rowAria(row: HubAwardRow, sportLabel: string, year: number): string {
  const value = row.kind === "average"
    ? `${row.value} out of 10 average${row.dataDays === null ? "" : ` over ${row.dataDays} days`}`
    : `${row.value} ${row.unit}${row.dataDays === null ? "" : ` out of ${row.dataDays} days`}`;
  return `${row.awardName} ${year}, ${sportLabel}: ${row.winnerName}, ${row.country}, ${value}. Open the award page`;
}

function BetaPill() {
  return (
    <span className="shrink-0 rounded-full border border-[#C9D3D6] px-1.5 text-[10px] font-bold uppercase leading-[15px] tracking-[0.04em] text-[#566072] md:border-0 md:bg-[#EDEFF2] md:px-[7px] md:py-px md:text-[10.5px] md:normal-case md:text-[#454C5A]">
      Beta
    </span>
  );
}

function BestBySport({
  awards,
  shown,
  selected,
  onPick,
}: {
  awards: HubAwards;
  shown: readonly HubSport[];
  selected: HubSport;
  onPick: (key: HubSportKey) => void;
}) {
  const baseId = useId();
  const selectId = `${baseId}-sport`;
  const headingId = `${baseId}-heading`;
  const liveId = `${baseId}-live`;
  const rows = awards.bySport[selected.key] ?? [];
  const note = awards.notes[selected.key];
  const options = new Set(awards.sportOptions);
  return (
    <div className="px-3 pt-[18px] md:px-5 md:pb-0.5 md:pt-4">
      <div className="flex items-center justify-between gap-2.5 px-1 md:gap-3 md:px-0">
        <h3 id={headingId} className="m-0 whitespace-nowrap text-[16px] font-bold text-navy md:text-[17px]">Best by sport</h3>
        <label htmlFor={selectId} className="sr-only">Show the best {awards.year} awards for</label>
        <span className="relative flex min-w-0 flex-1 items-center justify-end">
          <select
            id={selectId}
            value={selected.key}
            onChange={(event) => onPick(event.target.value as HubSportKey)}
            aria-describedby={liveId}
            className="min-h-11 w-full max-w-[200px] cursor-pointer appearance-none rounded-[10px] border border-[#848E86] bg-white py-0 pl-[11px] pr-[30px] font-sans text-[14.5px] font-semibold text-navy transition-colors hover:border-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
          >
            {HUB_SPORT_GROUPS.map((group) => {
              const inGroup = shown.filter((sport) => sport.group === group.key && options.has(sport.key));
              if (inGroup.length === 0) return null;
              return (
                <optgroup key={group.key} label={group.label}>
                  {inGroup.map((sport) => (
                    <option key={sport.key} value={sport.key}>
                      {sport.label}{sport.beta ? " (Beta)" : ""}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
          <span aria-hidden="true" className="pointer-events-none absolute right-2.5 flex text-teal-dark">
            <HubChevron direction="down" />
          </span>
        </span>
      </div>
      <p id={liveId} className="sr-only" aria-live="polite">Best by sport: {selected.label} winners for {awards.year}</p>
      <ul aria-labelledby={headingId} className="-mx-2 mb-0 mt-1.5 list-none p-0 md:min-h-[146px]">
        {rows.map((row, index) => (
          <li key={row.awardId} className={index < rows.length - 1 ? "border-b border-[#EDF1EC]" : undefined}>
            <Link
              href={row.href}
              prefetch={false}
              aria-label={rowAria(row, selected.label, awards.year)}
              className="group flex min-h-[76px] items-center gap-3 rounded-[14px] p-2.5 text-text-body no-underline transition-colors hover:bg-[#F4F8F7] hover:text-text-body md:grid md:min-h-[72px] md:grid-cols-[40px_minmax(0,1fr)_auto] md:rounded-[12px] md:px-2"
            >
              <span
                aria-hidden="true"
                className={`flex size-10 shrink-0 items-center justify-center rounded-[11px] text-teal-dark ${
                  index === 0 ? "bg-[#E4F2F3]" : "bg-[#E4F2F3] md:bg-[#EEF2EC]"
                }`}
              >
                <HubIcon name={selected.icon} size={21} strokeWidth={1.8} />
              </span>
              <span className="flex min-w-0 flex-auto flex-col gap-0.5">
                <span className="flex min-w-0 items-center gap-1.5 text-[12.5px] font-bold leading-[1.3] text-teal-dark md:text-[11.5px] md:uppercase md:tracking-[0.08em]">
                  {row.awardName}
                  {selected.beta && <BetaPill />}
                </span>
                <span className="truncate text-[15.5px] font-bold leading-[1.2] text-navy md:whitespace-normal md:text-[17px]">{row.winnerName}</span>
                <span className="text-[12.5px] leading-[1.3] text-[#566072] md:text-[13px] md:leading-[1.35]">
                  <span className="md:hidden">{row.subPhone}</span>
                  <span className="hidden md:inline">{row.sub}</span>
                </span>
              </span>
              <span aria-hidden="true" className="flex max-w-[104px] shrink-0 flex-col items-end gap-[3px] text-right md:gap-0.5">
                <span className="flex items-baseline gap-0.5">
                  <span className="text-[22px] font-bold leading-none text-navy">{row.value}</span>
                  {row.valueSuffix && <span className="text-[11.5px] font-semibold text-[#566072] md:text-[22px] md:font-bold md:leading-none md:text-navy">{row.valueSuffix}</span>}
                </span>
                <span className="max-w-[92px] text-[11.5px] font-medium leading-[1.25] text-[#566072] md:text-[12px] md:font-semibold md:leading-[1.3]">{row.unit}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {note && <p className="mx-1 mb-0 mt-0.5 text-[12px] leading-[1.45] text-[#566072] md:mx-0 md:text-[12.5px]">{note}</p>}
    </div>
  );
}

function funAria(fun: HubFunAward, index: number, total: number, year: number): string {
  return `Fun award ${index + 1} of ${total}, ${fun.awardName} ${year}: ${fun.winnerName}, ${fun.country}. ${fun.value} ${fun.unit}. ${fun.text} Open the award page`;
}

function Dots({ total, index, variant }: { total: number; index: number; variant: "desktop" | "phone" }) {
  return (
    <span aria-hidden="true" className="flex items-center gap-[5px]">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`block h-1.5 rounded-full transition-[width] duration-200 motion-reduce:transition-none ${
            i === index ? "w-4" : "w-1.5"
          } ${
            variant === "desktop"
              ? i === index ? "bg-[#8A6A2A]" : "bg-[#DCC89A]"
              : i === index ? "bg-teal" : "bg-[#C9D0C6]"
          }`}
        />
      ))}
    </span>
  );
}

function NextIcon({ size }: { size: number }) {
  return <HubChevron direction="right" size={size} />;
}

function FunAwards({ awards, index, onNext }: { awards: HubAwards; index: number; onNext: () => void }) {
  const baseId = useId();
  const fun = awards.fun;
  const current = fun[index];
  if (!current) return null;
  const next = fun[(index + 1) % fun.length];
  const many = fun.length > 1;
  const icon = SPORT_BY_LABEL.get(current.sportLabel)?.icon ?? "surf";
  const headingId = `${baseId}-h`;
  const desktopCardId = `${baseId}-card`;
  const phoneCardId = `${baseId}-phone`;
  const nextLabel = next ? `Next fun award: ${next.awardName}` : "Next fun award";
  const aria = funAria(current, index, fun.length, awards.year);
  return (
    <>
      {/* Desktop */}
      <div
        role="group"
        aria-labelledby={`${headingId}-d`}
        className="mx-5 mt-3 hidden rounded-[14px] border border-[#EFE3C6] bg-[#FBF6EA] pb-3 pl-4 pr-3 pt-2.5 md:block"
      >
        <div className="flex items-center gap-2.5">
          <h3 id={`${headingId}-d`} className="m-0 rounded-full bg-[#F3E6C8] px-[9px] py-0.5 text-[11.5px] font-bold uppercase tracking-[0.08em] text-[#6E4F14]">
            Fun award
          </h3>
          {many && <Dots total={fun.length} index={index} variant="desktop" />}
          <span className="sr-only" aria-live="polite">Fun award {index + 1} of {fun.length}: {current.awardName}</span>
          {many && (
            <button
              type="button"
              onClick={onNext}
              aria-label={nextLabel}
              aria-controls={desktopCardId}
              className="ml-auto flex min-h-11 min-w-11 cursor-pointer items-center gap-1.5 rounded-[10px] border border-[#D9C597] bg-white pl-3.5 pr-3 font-sans text-[14px] font-bold text-[#6E4F14] transition-colors hover:bg-[#F6EEDC] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
            >
              Next
              <NextIcon size={16} />
            </button>
          )}
        </div>
        <Link
          id={desktopCardId}
          href={current.href}
          prefetch={false}
          aria-label={aria}
          className="group mt-2 grid min-h-11 grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3 text-text-body no-underline hover:text-text-body"
        >
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-[11px] bg-[#F3E6C8] text-[#6E4F14]">
            <HubIcon name={icon} size={21} strokeWidth={1.8} />
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[11.5px] font-bold uppercase tracking-[0.08em] text-[#6E4F14]">{current.awardName}</span>
            <span className="text-[17px] font-bold leading-[1.2] text-navy">{current.winnerName}</span>
            <span className="text-[13px] leading-[1.35] text-[#566072]">{current.country} · {current.sportLabel}</span>
          </span>
          <span className="flex flex-col items-end gap-0.5 text-right">
            <span className="whitespace-nowrap text-[22px] font-bold leading-none text-navy">{current.value}</span>
            <span className="max-w-[92px] text-[12px] font-semibold leading-[1.3] text-[#566072]">{current.unit}</span>
          </span>
        </Link>
        <p className="mb-0 mt-1.5 text-[13px] leading-[1.4] text-[#4A4235]">{current.text}</p>
      </div>

      {/* Phone */}
      <div className="mx-3 mt-4 rounded-2xl border border-border bg-cream p-3.5 md:hidden">
        <div className="flex items-center justify-between gap-2.5">
          <h3 id={`${headingId}-p`} className="m-0 text-[12px] font-bold uppercase tracking-[0.12em] text-[#566072]">Fun award</h3>
          {many && <span className="text-[12.5px] font-semibold text-[#566072]">{index + 1} of {fun.length}</span>}
        </div>
        <div id={phoneCardId} aria-live="polite" className="mt-2.5">
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-[#E4F2F3] text-teal-dark">
              <HubIcon name={icon} size={20} strokeWidth={1.8} />
            </span>
            <div className="min-w-0 flex-auto">
              <p className="m-0 flex flex-wrap items-center gap-1.5">
                <span className="text-[17px] font-bold leading-[1.2] text-navy">{current.awardName}</span>
                <span
                  className={`shrink-0 rounded-full px-[7px] text-[10.5px] font-bold leading-[18px] ${
                    current.tag === "Roast" ? "bg-[#F5E8E2] text-[#8E4C30]" : "bg-[#E3F4EF] text-[#0A6E58]"
                  }`}
                >
                  {current.tag}
                </span>
              </p>
              <p className="mb-0 mt-0.5 text-[14px] font-semibold text-text-body">
                {current.winnerName}
                <span className="font-medium text-[#566072]">, {current.country}</span>
              </p>
            </div>
          </div>
          <p className="mb-0 mt-2.5 text-[14px] leading-[1.5] text-text-body">{current.text}</p>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <Link
            href={current.href}
            prefetch={false}
            aria-label={aria}
            className="group inline-flex min-h-11 items-center gap-1.5 text-[14px] font-bold text-teal-dark underline underline-offset-[3px]"
          >
            See the award
            <HubArrow />
          </Link>
          {many && (
            <div className="flex items-center gap-2.5">
              <Dots total={fun.length} index={index} variant="phone" />
              <button
                type="button"
                onClick={onNext}
                aria-label={nextLabel}
                aria-controls={phoneCardId}
                className="inline-flex h-11 min-w-11 cursor-pointer items-center gap-1.5 rounded-[12px] border border-navy bg-white px-3.5 font-sans text-[14px] font-bold text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
              >
                Next
                <NextIcon size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function HubAwardsCard({ awards, sports, namedPublic, boardSport }: HubAwardsCardProps) {
  const shared = useHubSport(namedPublic);
  const board = boardSport === undefined ? shared : boardSport;
  const [picked, setPicked] = useState<HubSportKey | null>(null);
  const [funIndex, setFunIndex] = useState(0);

  const shown = sports.filter((sport) => awards.sportOptions.includes(sport.key));
  const selectedKey = awardsSportFor(shown.map((sport) => sport.key), picked, board);
  const selected = selectedKey ? shown.find((sport) => sport.key === selectedKey) ?? null : null;
  const funTotal = awards.fun.length;
  const index = funTotal > 0 ? funIndex % funTotal : 0;

  return (
    <section
      aria-label={`Season awards ${awards.year}`}
      className="overflow-hidden rounded-[20px] border border-border bg-white md:shadow-[0_18px_40px_rgba(22,35,59,0.08)]"
    >
      <DesktopHeader awards={awards} />
      <PhoneHeader awards={awards} />
      {selected && <BestBySport awards={awards} shown={shown} selected={selected} onPick={setPicked} />}
      {funTotal > 0 && <FunAwards awards={awards} index={index} onNext={() => setFunIndex((index + 1) % funTotal)} />}
      <div className="px-3 pb-3 pt-3.5 md:px-5 md:pb-[18px]">
        <Link
          href={awards.allHref}
          prefetch={false}
          aria-label="See all season awards, every sport and period"
          className="group flex min-h-12 items-center justify-between gap-2.5 rounded-[12px] border border-[#BFDDE1] bg-[#F2F8F8] px-3.5 text-[14.5px] font-bold text-teal-dark no-underline hover:text-teal-dark md:min-h-[46px] md:justify-center md:gap-2 md:border-teal md:bg-transparent md:px-4 md:text-[15px]"
        >
          See all season awards
          <HubArrow />
        </Link>
      </div>
    </section>
  );
}
